import {
    Component,
    ElementRef,
    forwardRef,
    Input,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToolbarOptions, ToolbarSettings } from './text-editor-toolbar.types';
import Quill, { Delta } from 'quill';
import { TextEditorToolbarUtils } from './text-editor.utils';
import { FileHelper } from 'src/app/helpers/files/file.helper';
import { EventEmitter } from '@angular/core';

@Component({
    selector: 'app-text-editor',
    templateUrl: './text-editor.component.html',
    styleUrls: ['./text-editor.component.scss'],
    standalone: true,
    imports: [CommonModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextEditorComponent),
            multi: true,
        },
    ],
})
export class TextEditorComponent implements OnInit {
    @Input() content: string = 'your content here';
    @Input() placeholder: string;
    // Use the defined type for toolbar options
    @Input() toolbarOptions: ToolbarOptions | false = TextEditorToolbarUtils.BASIC_TOOLBAR;
    @Input() toolbarSettings: ToolbarSettings = { disableToolbar: false, disableImageDrop: false, disableTextUpdateOnChange: false };

    @Input() height: string;

    @Output() blur: EventEmitter<any> = new EventEmitter();

    @ViewChild('editor') editorElement: ElementRef;

    editorHeight: string = '200px';
    editor: any;
    value: string = '';
    disabled: boolean = false;
    onChange: any = () => {};
    onTouched: any = () => {};

    constructor(private fileHelper: FileHelper) {}

    ngOnInit(): void {
        if(this.toolbarSettings.disableToolbar || this.toolbarOptions == false){
            this.toolbarOptions = false;
            return;
        }
    }

    ngAfterViewInit() {
        this.initialize();
    }
    ngOnChanges(changes: SimpleChanges) {
        if (changes.height) {
            this.editorHeight = changes.height.currentValue || '100%';
        }
    }

    async initialize() {
        // Dynamically import ImageResize module (compatible with Quill 2.0)
        const ImageResize = (await import("@mgreminger/quill-image-resize-module")).default;
        
        // Register the ImageResize module with Quill
        Quill.register('modules/imageResize', ImageResize);

        this.editor = new Quill(this.editorElement.nativeElement, {
            modules: {
                imageResize: {
                    modules: ["Resize", "DisplaySize"]
                },
                toolbar: (!this.toolbarOptions) ? false : {
                    container: this.toolbarOptions,
                    handlers: {
                        image: function () {
                            const input = document.createElement('input');
                            input.setAttribute('type', 'file');
                            input.setAttribute('accept', 'image/*');
                            input.click(); // Open file explorer

                            input.onchange = () => {
                                const file = input.files?.[0];

                                if (!file) return;

                                if (
                                    !this.fileHelper.isValidImageSize(file.size)
                                )
                                    return;

                                // Convert image to base64 and insert
                                const reader = new FileReader();
                                reader.onload = () => {
                                    const range = this.editor.getSelection();
                                    this.editor.insertEmbed(
                                        range.index,
                                        'image',
                                        reader.result as string
                                    );
                                };
                                reader.readAsDataURL(file);
                            };
                        }.bind(this),
                    },
                },
            },
            placeholder: this.placeholder,
            theme: 'snow',
        });  

        if(this.toolbarSettings.disableImageDrop) {
            this.editor.root.addEventListener('dragover', (event) => {
                event.preventDefault();
                event.stopPropagation();
                event.dataTransfer.dropEffect = 'none';
                return false;
              }, false);
        }

        if(this.toolbarSettings.disableImagePaste) {
            this.editor.root.addEventListener('paste', (event: ClipboardEvent) => { 
                const hasImageFiles = Array.from(event.clipboardData.items).some(
                    item => item.type.startsWith('image')
                  );

                if(hasImageFiles) {
                    event.preventDefault();
                    event.stopPropagation();  
                    return true;
                }

                return false;
            }, true);
        }

        // Prevent large images from being dropped
        this.editor.root.addEventListener('drop', (event: DragEvent) => {
            const file = event.dataTransfer?.files?.[0];
            if (this.fileHelper.isValidImageSize(file.size)) return true;
            event.preventDefault(); // Stop image insertion
            event.stopPropagation();
            return false;
        },true);

        this.editor.on('text-change', () => {
            if(this.toolbarSettings.disableTextUpdateOnChange) return;
            this.emitTextChange();
        });

        this.editor.on('selection-change', (range: any) => {
            if (!range) {
                if(this.toolbarSettings.disableTextUpdateOnChange){
                   this.emitTextChange();
                }
                this.blur.emit(range);
            }
            this.onTouched();
        });

        if (this.value) {
            this.editor.clipboard.dangerouslyPasteHTML(this.value);
        }
    }
    emitTextChange(){
        const rawHtml =
                this.editorElement.nativeElement.querySelector(
                    '.ql-editor'
                ).innerHTML;
        const html = this.normalizeQuillListHtml(rawHtml);
        this.onChange(html);
    }

    /**
     * Quill 2.0 renders all lists as <ol> with data-list attributes on <li> elements
     * (e.g. data-list="bullet" for unordered, data-list="ordered" for ordered).
     * This works inside the editor via Quill's CSS, but breaks outside it (e.g. in emails).
     * Convert bullet lists to proper <ul> and remove data-list attributes.
     */
    private normalizeQuillListHtml(html: string): string {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        tempDiv.querySelectorAll('ol').forEach(ol => {
            const items = Array.from(ol.querySelectorAll(':scope > li'));
            const allBullet = items.length > 0 && items.every(li => li.getAttribute('data-list') === 'bullet');

            if (allBullet) {
                const ul = document.createElement('ul');
                while (ol.firstChild) {
                    ul.appendChild(ol.firstChild);
                }
                ol.parentNode?.replaceChild(ul, ol);
            }
        });

        // Clean Quill-specific data-list attributes from all list items
        tempDiv.querySelectorAll('li[data-list]').forEach(li => li.removeAttribute('data-list'));

        return tempDiv.innerHTML;
    }
    writeValue(value: string): void {
        this.value = value || '';

        if (this.editor) {
            if (value) {
                this.editor.clipboard.dangerouslyPasteHTML(value);
            } else {
                this.editor.setText('');
            }
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (this.editor) {
            this.editor.enable(!isDisabled);
        }
    }
}
